/**
 * SNAP-2338: shared origin-validation helpers for the postMessage channels between this app,
 * its own iframes and the browser extension content script.
 *
 * `event.origin` is the browser's own record of who actually sent the message — unlike a
 * `source` field carried inside the message payload, a page cannot forge it. Any listener
 * that trusts a payload-reported field instead of `event.origin` accepts messages from any
 * website, since the extension's content script runs on <all_urls>.
 */

/**
 * True for a message posted by this app's own window (e.g. the extension content script,
 * which shares this page's origin) into itself. Not for messages from an iframe embedded in
 * this page — use `isTrustedIframeMessage` for those.
 */
export function isSameOriginMessage(event: MessageEvent): boolean {
  return event.source === window && event.origin !== 'null' && event.origin === window.location.origin;
}

/**
 * The extension recorder iframe's origin, derived from the extension's own `baseUrl`
 * (`chrome-extension://<id>/`) rather than `new URL(baseUrl).origin` — the WHATWG URL parser
 * returns the opaque origin `'null'` for `chrome-extension:` URLs, which would never match
 * `event.origin` and would make the check silently reject every legitimate message.
 */
export function extensionIframeOrigin(baseUrl: string): string {
  return baseUrl.replace(/\/+$/, '');
}

/**
 * True for a message whose origin matches the extension's own iframe origin. The iframe's
 * `event.source` is its own `contentWindow`, not this page's `window`, so this intentionally
 * does not gate on `event.source`.
 */
export function isTrustedIframeMessage(event: MessageEvent, baseUrl: string): boolean {
  if (!baseUrl) return false;
  return event.origin === extensionIframeOrigin(baseUrl);
}
